import React, { useState, useEffect } from "react";
import { PageLoader, Button } from "@vionixasia/shared-components";
import { useNavigate, Link } from "react-router-dom";
import { getFirestore, doc, onSnapshot } from "firebase/firestore";
import moment from "moment-timezone";
import { getOptimizedEventImage } from "../utils/imageUtils";
import { eventBadge } from "../utils/eventStatus";
import { useEventImage } from "../utils/useEventImage";
import { motion } from "framer-motion";
import { FiArrowRight, FiCalendar, FiChevronLeft, FiChevronRight, FiMapPin } from "react-icons/fi";


const getHeroImage = (heroImage) => {
  return heroImage || "/bg/smm.webp";
};

const formatEventDate = (event) => {
  const raw = event?.date || event?.categories?.[0]?.startTime;
  const day = raw ? moment(raw?.toDate ? raw.toDate() : raw) : null;
  return day?.isValid() ? day.format("DD MMM YYYY") : null;
};

// One slide: the event's own photo when it has one, else the timing backdrop.
const HeroSlide = ({ event, active }) => {
  const imageSrc = useEventImage(event?.eventId, event?.heroImage);
  return (
    <img
      src={imageSrc || "/bg/bghero1.jpg"}
      alt=""
      aria-hidden="true"
      className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${active ? "opacity-100" : "opacity-0"}`}
    />
  );
};

const SLIDE_MS = 7000;

/**
 * Homepage hero: the events, one per slide, over their own photo — name, date,
 * place and a way in. With no events yet it shows the platform itself.
 */
export const Hero = ({ events = [] }) => {
  const slides = events.slice(0, 5);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = slides.length;

  useEffect(() => {
    if (index >= count) setIndex(0);
  }, [count, index]);

  useEffect(() => {
    if (count < 2 || paused) return undefined;
    const timer = setInterval(() => setIndex((i) => (i + 1) % count), SLIDE_MS);
    return () => clearInterval(timer);
  }, [count, paused]);

  const go = (step) => setIndex((i) => (i + step + count) % count);
  const event = slides[index];
  const date = event ? formatEventDate(event) : null;
  const badge = event ? eventBadge(event) : null;
  const scrollToEvents = () =>
    document.getElementById("events")?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div
      className="relative w-full h-[65vh] md:h-[65vh] overflow-hidden bg-slate-900"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {count ? (
        slides.map((e, i) => <HeroSlide key={e.eventId} event={e} active={i === index} />)
      ) : (
        <img src="/bg/bghero1.jpg" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/50 to-black/30" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/20 to-transparent" />

      <div className="absolute inset-0">
        <div className="container mx-auto px-4 h-full flex flex-col justify-end pb-8 md:pb-10">
          <motion.div
            key={event?.eventId || "platform"}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl"
          >
            {badge && (
              <span
                className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wider backdrop-blur ${
                  badge === "LIVE NOW" ? "bg-red-500/90 text-white" : "bg-white/15 text-white"
                }`}
              >
                {badge === "LIVE NOW" && <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" />}
                {badge}
              </span>
            )}
            <h1 className="[text-shadow:0_1px_14px_rgba(0,0,0,0.55)] mt-4 text-2xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white leading-tight line-clamp-3">
              {event ? event.eventName : "Timing VionixAsia"}
            </h1>
            {event ? (
              <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm md:text-base text-white/90 [text-shadow:0_1px_10px_rgba(0,0,0,0.6)]">
                {date && (
                  <span className="inline-flex items-center gap-2">
                    <FiCalendar className="shrink-0" aria-hidden="true" />
                    {date}
                  </span>
                )}
                <span className="inline-flex items-center gap-2">
                  <FiMapPin className="shrink-0" aria-hidden="true" />
                  {event.location || "Indonesia"}
                </span>
              </div>
            ) : (
              <p className="mt-5 max-w-2xl text-sm md:text-base text-white/85 [text-shadow:0_1px_10px_rgba(0,0,0,0.6)]">
                Innovative digital timing platform for endurance events. Accurate real-time results for participants and organizers.
              </p>
            )}
            <div className="mt-7 flex flex-wrap items-center gap-3">
              {event && (
                <Button as={Link} to={`/${event.slug || event.eventId}`} variant="muted">
                  {badge === "UPCOMING" ? "View event" : "View results"}
                  <FiArrowRight aria-hidden="true" />
                </Button>
              )}
              <Button variant="glass" onClick={scrollToEvents}>
                All events
              </Button>
            </div>
          </motion.div>

          {count > 1 && (
            <div className="mt-8 md:mt-10 flex items-center justify-end gap-3">
              <div className="hidden sm:flex items-center gap-1.5">
                {slides.map((e, i) => (
                  <button
                    key={e.eventId}
                    type="button"
                    onClick={() => setIndex(i)}
                    aria-label={`Show ${e.eventName}`}
                    className={`h-1.5 rounded-full transition-all ${i === index ? "w-8 bg-white" : "w-1.5 bg-white/50 hover:bg-white/80"}`}
                  />
                ))}
              </div>
              <span className="text-xs md:text-sm text-white/80 tabular-nums">{index + 1} / {count}</span>
              <Button variant="glass" onClick={() => go(-1)} aria-label="Previous event">
                <FiChevronLeft aria-hidden="true" />
              </Button>
              <Button variant="glass" onClick={() => go(1)} aria-label="Next event">
                <FiChevronRight aria-hidden="true" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const EventCard = ({ eventId, eventName, categories, date, status, heroImage, location, dataSource }) => {

  const navigate = useNavigate();
  const imageSrc = useEventImage(eventId, heroImage);

  const eventStatus = eventBadge({ status, categories, date });

  return (
    <div
      className="w-full bg-white rounded-card border border-slate-200 overflow-hidden cursor-pointer"
      onClick={() => {
        navigate(`/${eventId}`);
      }}
    >
      <div className="flex flex-col lg:grid lg:grid-cols-12 h-auto">
        <div className="relative h-48 lg:col-span-3 lg:h-auto bg-gray-50">
          <img
            src={imageSrc}
            alt={eventName}
            className="lg:absolute lg:inset-0 w-full h-full object-cover"
            fetchpriority="high"
            loading="eager"
            decoding="sync"
          />
        </div>
        <div className="lg:col-span-9 p-4 flex flex-col gap-3">
          <div>
            <h3 className="text-xl sm:text-2xl font-black text-slate-700 uppercase leading-tight">
              {eventName}
            </h3>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-card px-4 pt-4 pb-2 relative mt-auto">
            {/* Status Badge */}
            <div className="absolute top-4 right-4">
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide border ${
                  eventStatus === "LIVE NOW"
                    ? "bg-red-100 text-red-800 border-red-200"
                    : eventStatus === "UPCOMING"
                    ? "bg-blue-100 text-blue-800 border-blue-200"
                    : "bg-amber-100 text-amber-800 border-amber-200"
                }`}
              >
                {eventStatus}
              </span>
            </div>

            <div className="flex flex-col md:flex-row gap-4 md:gap-12">
              <div className="flex flex-col">
                <span className="text-xs text-slate-500 font-semibold">Date</span>
                <span className="text-slate-700">
                  {/* The event's own date is the source of truth. Reading the
                      first category's startTime showed "TBA" for buzzer-timed
                      races, which legitimately have no gun start at all. */}
                  {(() => {
                    const day = date
                      ? moment(date)
                      : categories?.[0]?.startTime
                        ? moment(categories[0].startTime)
                        : null;
                    return day?.isValid() ? day.format("DD MMMM YYYY") : "TBA";
                  })()}
                </span>
              </div>
              <div className="flex flex-col">
                <span className="text-xs text-slate-500 font-semibold">Location</span>
                <span className="text-slate-700">
                  {location || "Indonesia"}
                </span>
              </div>
            </div>
            {categories.length > 0 && (
              <div className="mt-3 pt-2 border-t border-slate-200 font-semibold text-gray-500 text-xs sm:text-sm leading-relaxed hidden sm:block">
                {categories.map((c) => c.name).join(" • ")}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const SectionHero = ({ eventId }) => {
  const [state, setState] = useState({
    categories: [],
    eventName: "Loading...",
    heroImage: "/bg/smm.webp",
    status: "ongoing",
    loading: true,
  });

  useEffect(() => {
    if (!eventId) return;

    setState((prev) => ({ ...prev, loading: true }));

    const eventRef = doc(getFirestore(), "event_timing", eventId);
    const unsubscribe = onSnapshot(eventRef, (doc) => {
      if (doc.exists()) {
        const eventData = doc.data();
        const fixedEventName = eventData.eventName || "Unknown Event";
        setState({
          categories: eventData.categories || [],
          eventName: fixedEventName,
          heroImage: eventData.heroImage || "/bg/smm.webp",
          location: eventData.location || "Indonesia",
          date: eventData.date || null,
          status: eventData.status || "ongoing",
          loading: false,
        });
      } else {
        setState((prev) => ({ ...prev, loading: false, eventName: "Event Not Found" }));
      }
    });

    return () => unsubscribe();
  }, [eventId]);

  if (state.loading) {
    return <PageLoader />;
  }

  return (
    <section className="min-h-screen mt-10 bg-white mb-10">
      <div className="container mx-auto px-4">
        <Hero heroImage={state.heroImage} />
        <EventCard
          eventId={eventId}
          eventName={state.eventName}
          categories={state.categories}
          date={state.date}
          status={state.status}
          heroImage={state.heroImage}
          location={state.location}
        />
      </div>
    </section>
  );
};

export default SectionHero;
